import React, { useEffect, useState } from 'react';
import { Input as AntInput, Select } from 'antd';
import Flag from '../Flag/Flag';
import { t } from '../../i18n/i18n';
import {
  DEFAULT_DIAL_CODE,
  findCountry,
  findCountryByDialCode,
  SERVED_COUNTRIES,
} from '../../utilities/country';
import './NumberInput.scss';

/** E.164's ceiling — the longest national number any country uses. */
const MAX_PHONE_DIGITS = 15;

const normalize = (input) => input.trim().toLowerCase().replace(/^\+/, '');

/**
 * How well a country answers what was typed — lower is better, `null` is no
 * match. A bare "44" puts the UK (the dial code's primary) ahead of Guernsey,
 * so Enter and click-away both land on the country the digits mean.
 */
const matchRank = (country, query) => {
  if (!query) return 0;
  const digits = country.dialCode.slice(1);
  if (digits === query) {
    return findCountryByDialCode(country.dialCode)?.code === country.code ? 0 : 1;
  }
  if (digits.startsWith(query)) return 2;
  // The reader's language and English both, as in the country Select.
  const name = `${t(country.name)} ${country.name}`.toLowerCase();
  if (name.startsWith(query)) return 3;
  if (name.includes(query)) return 4;
  return null;
};

const bestMatch = (input, countries = SERVED_COUNTRIES) => {
  const query = normalize(input);
  if (!query) return null;
  let best = null;
  let bestRank = Infinity;
  countries.forEach((c) => {
    const rank = matchRank(c, query);
    if (rank !== null && rank < bestRank) {
      best = c;
      bestRank = rank;
    }
  });
  return best;
};

export default function NumberInput({
  label,
  placeholder,
  value,
  onChange,
  name,
  error,
  required,
  disabled,
  countryCode = DEFAULT_DIAL_CODE,
  onCountryCodeChange,
  // Optional ISO code saying which territory a shared dial code means — the
  // sign-up form's chosen country, so picking Jersey shows Jersey's flag on
  // "+44" rather than the UK's.
  countryIso,
  // The countries the dial-code list offers: only the ones we serve, wherever
  // a phone number is taken — banned countries are never offered.
  countries = SERVED_COUNTRIES,
  ...props
}) {
  /**
   * Options are keyed by ISO code, not dial code — 26 territories share "+1",
   * and duplicate option values leave antd unable to say which row is selected.
   * The parent still only ever hears about the dial code, so remember the picked
   * territory here to keep the right flag showing; a `countryCode` that no
   * longer matches it means the parent changed the prefix itself, so fall back
   * to that dial code's primary country.
   */
  const [pickedCode, setPickedCode] = useState(null);
  // A new country from the parent outranks a territory picked here earlier.
  useEffect(() => {
    setPickedCode(null);
  }, [countryIso]);
  const picked = findCountry(pickedCode);
  const hinted = findCountry(countryIso);
  const selected =
    picked?.dialCode === countryCode
      ? picked
      : hinted?.dialCode === countryCode
        ? hinted
        : findCountryByDialCode(countryCode);

  // antd drops typed search text on blur in single mode, so "351" then a click
  // into the number field used to snap back to +44. Keep the text to commit it.
  const [search, setSearch] = useState('');

  const pick = (code) => {
    setSearch('');
    setPickedCode(code);
    onCountryCodeChange?.(findCountry(code)?.dialCode ?? countryCode);
  };

  // Phone prefix selector component using Ant Design's Select
  const phonePrefixSelector = (
    <div
      className="phone-prefix-wrapper"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <Select
        value={selected?.code}
        onChange={pick}
        onBlur={() => {
          const match = bestMatch(search, countries);
          if (match) pick(match.code);
          else setSearch('');
        }}
        className="phone-prefix-select"
        classNames={{ popup: { root: 'phone-prefix-popup' } }}
        variant="borderless"
        disabled={disabled}
        // The collapsed control has room for the prefix only; the open list
        // needs the country name to be pickable at all. A fixed width, not
        // `false` — `false` switches off virtual scrolling, and rendering all
        // 249 flag rows at once held the list back for seconds after a click.
        popupMatchSelectWidth={320}
        optionLabelProp="prefix"
        // 249 territories is too many to scroll, so let the user type.
        showSearch={{
          onSearch: setSearch,
          filterOption: (input, option) => matchRank(option.country, normalize(input)) !== null,
          filterSort: (a, b, { searchValue }) => {
            const query = normalize(searchValue);
            return matchRank(a.country, query) - matchRank(b.country, query);
          },
        }}
        options={countries.map((c) => ({
          value: c.code,
          country: c,
          prefix: (
            <span className="phone-prefix-option-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '100%' }}>
              <Flag code={c.code} className="phone-prefix-flag-img" />
              <span style={{ color: 'var(--text-primary, #f7f8f8)' }}>{c.dialCode}</span>
            </span>
          ),
          label: (
            <span className="phone-prefix-option-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '100%' }}>
              <Flag code={c.code} className="phone-prefix-flag-img" />
              <span style={{ color: 'var(--text-primary, #f7f8f8)' }}>{c.dialCode}</span>
              <span style={{ color: 'var(--text-secondary, #8a8f98)' }}>{t(c.name)}</span>
            </span>
          ),
        }))}
      />
    </div>
  );

  return (
    <div className={`reusable-input-group ${error ? 'has-error' : ''} ${disabled ? 'is-disabled' : ''}`}>
      {label && <label className="reusable-input-label">{t(label)}</label>}

      <AntInput
        type="tel"
        prefix={phonePrefixSelector}
        className="reusable-antd-input reusable-antd-input--phone"
        placeholder={t(placeholder)}
        value={value}
        // Digits only: anything else typed or pasted ("+44 7700-900 123",
        // letters) is dropped before the form hears about it. The dial code
        // lives in the prefix select, so the number never needs a "+".
        // Capped at 15 *digits* here rather than with `maxLength`: the browser
        // applies `maxLength` to the raw text, so a pasted "+44 (7700) 900-123"
        // was cut at its fifteenth character — spaces and brackets included —
        // before the non-digits were dropped.
        onChange={(e) =>
          onChange?.({
            target: { name, value: e.target.value.replace(/\D/g, '').slice(0, MAX_PHONE_DIGITS) },
          })
        }
        // A phone's number pad rather than its full keyboard.
        inputMode="numeric"
        name={name}
        disabled={disabled}
        required={required}
        status={error ? 'error' : ''}
        {...props}
      />

      {error && <span className="reusable-input-error-text">{t(error)}</span>}
    </div>
  );
}
